"use client";

import { useEffect, useState, useRef } from "react";
import {
  Mail,
  Phone,
  MapPin,
  Twitter,
  Instagram,
  Linkedin,
  Facebook,
  Building,
} from "lucide-react";
import BannerSection from "./BannerSection";
import pageData from "../components/pageData.json";
import ReCAPTCHA from "react-google-recaptcha";
import Link from "next/link";

export default function ContactSection() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
  });

  const [settings, setSettings] = useState<{
    contact_phone?: string;
    contact_email?: string;
    contact_address?: string;
    twitter_url?: string;
    facebook_url?: string;
    instagram_url?: string;
    linkedin_url?: string;
  } | null>(null);

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });

  // ReCAPTCHA ref and token
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  // Fetch contact info from API
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch("https://portal.renvixx.com/api/settings", {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const contentType = res.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          throw new Error("Received non-JSON response from server");
        }
        const data = await res.json();
        if (data.success) setSettings(data.data);
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const { firstName, lastName, email, message } = formData;

    if (!firstName || !lastName || !email || !message) {
      setStatus({
        type: "error",
        message: "Please fill in all required fields.",
      });
      return;
    }

    if (!recaptchaToken) {
      setStatus({
        type: "error",
        message: "Please verify that you are not a robot.",
      });
      return;
    }

    setLoading(true);
    setStatus({ type: "", message: "" });

    try {
      const response = await fetch("https://portal.renvixx.com/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          email,
          message,
          recaptchaToken, // frontend-only token
        }),
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Received non-JSON response from server");
      }

      const result = await response.json();

      if (result.success) {
        setStatus({
          type: "success",
          message: "✅ Your message has been sent successfully!",
        });
        setFormData({ firstName: "", lastName: "", email: "", message: "" });
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
      } else {
        setStatus({
          type: "error",
          message:
            result.message || "❌ Failed to send message. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error submitting contact form:", error);
      setStatus({
        type: "error",
        message: "⚠️ Something went wrong. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  const data = pageData.contact;

  return (
    <>
      <BannerSection data={data} />
      <section className="max-w-7xl mx-auto relative pb-12 px-6 lg:px-8 -mt-[60px]">
        <div className="bg-white rounded-xl shadow-xl p-6 sm:p-10 grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          {/* Left Side: Contact Info */}
          <div>
            <h2 className="text-2xl font-bold text-[#2a6380] mb-6">
              Contact Us
            </h2>
            <div className="space-y-6">
              {/* Company Info */}
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#2a6380] text-[#FFF] shrink-0">
                  <Building className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 text-base">NAM ENTERPRISES (SMC-PRIVATE) LIMITED</h3>
                  <p className="text-gray-600 text-sm mt-0.5">
                    Operating as <strong>Renvixx</strong>
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#2a6380] text-[#FFF] shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Email Now</h3>
                  <p className="text-gray-600 text-sm">
                    {settings?.contact_email || "info@renvixx.com"}
                  </p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#2a6380] text-[#FFF] shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Call Us</h3>
                  <p className="text-gray-600 text-sm">
                    {settings?.contact_phone || "+92 33256 96 607"}
                  </p>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#2a6380] text-[#FFF] shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Address</h3>
                  <p className="text-gray-600 text-sm leading-snug">
                    {settings?.contact_address ||
                      "Flat# 104-B, 87-East Azeem Mansion, Fazal-ul-Haq Road, Blue Area, Islamabad, Pakistan"}
                  </p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="mt-8">
              <p className="text-gray-800 font-semibold mb-3">Follow Us</p>
              <div className="flex space-x-4 text-[#2a6380] text-xl">
                {[
                  {
                    name: "Twitter",
                    url: settings?.twitter_url,
                    icon: <Twitter className="w-6 h-6" />,
                  },
                  {
                    name: "Instagram",
                    url: settings?.instagram_url,
                    icon: <Instagram className="w-6 h-6" />,
                  },
                  {
                    name: "LinkedIn",
                    url: settings?.linkedin_url,
                    icon: <Linkedin className="w-6 h-6" />,
                  },
                  {
                    name: "Facebook",
                    url: settings?.facebook_url,
                    icon: <Facebook className="w-6 h-6" />,
                  },
                ].map((social) => (
                  <a
                    key={social.name}
                    href={social.url || "#"}
                    target={social.url ? "_blank" : undefined}
                    rel={social.url ? "noopener noreferrer" : undefined}
                    className={`transition ${
                      social.url
                        ? "hover:text-[#33c1c7]"
                        : "text-gray-400 cursor-not-allowed"
                    }`}
                    aria-label={social.name}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <a
                href="/pdfs/renvixx.pdf"
                download
                className="relative overflow-hidden inline-block border-2 border-[#2a6380] bg-[#2a6380] text-[#FFF] text-sm font-semibold px-8 py-3 rounded transition-all duration-500 ease-in-out group"
              >
                <span className="relative z-10 transition-colors duration-500 group-hover:text-[#2a6380]">
                  Download Company Profile
                </span>

                <span className="absolute inset-0 scale-[-1] bg-white translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500 ease-in-out"></span>
              </a>
            </div>
          </div>

          {/* Right Side: Contact Form */}
          <div>
            <h2 className="text-2xl font-bold text-[#2a6380] mb-6">
              Send Us a Message
            </h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">
                    First Name*
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="Ex. John"
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-1 focus:ring-[#33c1c7] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Last Name*
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Ex. William"
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-1 focus:ring-[#33c1c7] focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">
                  Email*
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="abc111@gmail.com"
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-1 focus:ring-[#33c1c7] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">
                  Message*
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Enter here..."
                  required
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-1 focus:ring-[#33c1c7] focus:outline-none"
                />
              </div>

              {/* ✅ reCAPTCHA */}
              <div>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ""}
                  onChange={(token) => setRecaptchaToken(token)}
                  onExpired={() => setRecaptchaToken(null)}
                />
              </div>

              {/* Status Message */}
              {status.message && (
                <p
                  className={`text-sm font-medium ${status.type === "success" ? "text-green-600" : "text-red-600"}`}
                >
                  {status.message}
                </p>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className={`relative overflow-hidden inline-block border-2 border-[#33c1c7] bg-[#33c1c7] text-[#FFF] text-sm font-semibold px-8 py-3 rounded transition-all duration-500 ease-in-out group ${loading ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <span className="relative z-10 transition-colors duration-500 group-hover:text-[#2a6380]">
                  {loading ? "Sending..." : "Send Message"}
                </span>
                <span className="absolute inset-0 scale-[-1] bg-white translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500 ease-in-out"></span>
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
